import { useEffect, useRef } from 'react';
import { FiArrowRight } from 'react-icons/fi';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import discover1 from '../assets/discover/1.png';
import discover2 from '../assets/discover/2.png';
import discover3 from '../assets/discover/3.png';
import discover4 from '../assets/discover/4.png';
import './DiscoverMore.css';

gsap.registerPlugin(ScrollTrigger);

const discoverItems = [
  {
    image: discover1,
    title: 'Dog Supplements',
    description: 'Targeted nutrition for every stage.',
    buttonText: 'Shop Supplements',
    link: '/collections/supplements',
    alt: 'Trillium Paws dog supplements',
  },
  {
    image: discover2,
    title: 'Toys & Accessories',
    description: 'Play, explore, chew and stay happy.',
    buttonText: 'Shop Toys',
    link: '/collections/toys',
    alt: 'Trillium Paws dog toys and accessories',
  },
  {
    image: discover3,
    title: 'Grooming & Home Care',
    description: 'Keep them fresh, clean, and comfortable.',
    buttonText: 'Shop Grooming',
    link: '/collections/grooming',
    alt: 'Trillium Paws grooming and home care products',
  },
  {
    image: discover4,
    title: 'Travel & Essentials',
    description: 'On-the-go solutions for every adventure.',
    buttonText: 'Shop Essentials',
    link: '/collections/essentials',
    alt: 'Trillium Paws dog travel essentials',
  },
];

export default function DiscoverMore() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const motion = gsap.matchMedia();
    motion.add('(prefers-reduced-motion: no-preference)', () => {
      const reveal = gsap.timeline({
        defaults: { ease: 'power3.out' },
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 92%',
          once: true,
          invalidateOnRefresh: true,
        },
      });

      reveal
        .fromTo('.discover-more__eyebrow', { autoAlpha: 0, y: 18 }, { autoAlpha: 1, y: 0, duration: .45 })
        .fromTo('.discover-more h2', { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: .6 }, '-=.22')
        .fromTo('.discover-more__header > p:last-child', { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: .5 }, '-=.28');

      // Separate the scroll reveal from the card's CSS hover transform.
      sectionRef.current.querySelectorAll('.discover-more__reveal').forEach(wrapper => {
        gsap.fromTo(wrapper,
          { autoAlpha: 0, y: 30 },
          {
            autoAlpha: 1, y: 0, duration: .65, ease: 'power3.out',
            clearProps: 'opacity,visibility,transform',
            scrollTrigger: { trigger: wrapper, start: 'top 92%', once: true },
          }
        );
      });

      const refreshTrigger = () => ScrollTrigger.refresh();
      const images = sectionRef.current.querySelectorAll('img');
      images.forEach(image => image.addEventListener('load', refreshTrigger));
      const refreshFrame = requestAnimationFrame(refreshTrigger);

      return () => {
        cancelAnimationFrame(refreshFrame);
        images.forEach(image => image.removeEventListener('load', refreshTrigger));
      };
    }, sectionRef);

    return () => motion.revert();
  }, []);

  return (
    <section className="discover-more" ref={sectionRef} aria-labelledby="discover-more-heading">
      <div className="header-container discover-more__container">
        <header className="discover-more__header">
          <p className="discover-more__eyebrow"><span aria-hidden="true" />DISCOVER MORE<span aria-hidden="true" /></p>
          <h2 id="discover-more-heading">More Ways to Care</h2>
          <p>Everything your dog needs for a healthy, happy, adventurous life.</p>
        </header>

        <div className="discover-more__grid">
          {discoverItems.map(({ image, title, description, buttonText, link, alt }) => (
            <div className="discover-more__reveal" key={title}>
            <article className="discover-more__card">
              <div className="discover-more__image-wrap">
                <img src={image} alt={alt} />
              </div>
              <div className="discover-more__content">
                <h3>{title}</h3>
                <p>{description}</p>
                <a href={link}>{buttonText} <FiArrowRight aria-hidden="true" /></a>
              </div>
            </article>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
